// src/helpers/tabHelpers.js
import { renameDoc } from './docHelpers';
import { uuid } from './uuid';
import { docMapKey, findItem } from './mapKey';

const makeCollisionSafeTabId = (baseId, existingIds) => {
  const normalizedBase = String(baseId || `tab-${uuid()}`);
  if (!existingIds || !existingIds.has(normalizedBase)) return normalizedBase;

  let suffix = 2;
  let candidate = `${normalizedBase}-${suffix}`;
  while (existingIds.has(candidate)) {
    suffix += 1;
    candidate = `${normalizedBase}-${suffix}`;
  }
  return candidate;
};

// Create a new tab object for a document
export const createTab = doc => ({
  id: doc && doc.isVersion ? uuid() : `tab-${String(doc?.id ?? uuid())}`,
  docId: doc.id,
  label: doc.name || 'Untitled Doc',
  readOnly: Boolean(doc.readOnly) || doc.docType === 'chat',
  toolbarVariant: doc.toolbarVariant || null,
  docType: doc.docType || null
});

// Add a tab, returns updated tabs and optionally sets new activeTabId
export const addTab = (tabs, doc) => {
  if (!doc) return { tabs, activeTabId: null };
  const newTab = createTab(doc);
  return { tabs: [...tabs, newTab], activeTabId: newTab.id };
};

// Close a tab, returns updated tabs and new activeTabId if needed
export const closeTab = (tabs, activeTabId, tabIdToClose) => {
  const closingIndex = tabs.findIndex(t => t.id === tabIdToClose);
  const remaining = tabs.filter(t => t.id !== tabIdToClose);

  let newActiveTabId = activeTabId;

  if (activeTabId === tabIdToClose) {
    if (remaining.length === 0) {
      newActiveTabId = null;
    } else {
      const previousIndex = closingIndex - 1;
      const nextIndex = closingIndex;

      if (previousIndex >= 0 && remaining[previousIndex]) {
        newActiveTabId = remaining[previousIndex].id;
      } else if (remaining[nextIndex]) {
        newActiveTabId = remaining[nextIndex].id;
      } else {
        newActiveTabId = remaining[remaining.length - 1].id;
      }
    }
  }

  return { tabs: remaining, activeTabId: newActiveTabId };
};

// Update a tab label and rename the underlying document
export const updateTabLabel = async (tabs, setTabs, tabId, newLabel, itemsMap, setItemsMap, docId) => {
  if (!docId) throw new Error('updateTabLabel requires numeric docId');

  // update the tab label in UI
  setTabs(prevTabs => prevTabs.map(t => t.id === tabId ? { ...t, label: newLabel } : t));

  // rename underlying document
  await renameDoc(docId, newLabel, itemsMap, setItemsMap, tabs, setTabs);

  // update ProjectPanel itemsMap if passed
  if (itemsMap instanceof Map && itemsMap.has(docMapKey(docId))) {
    const item = itemsMap.get(docMapKey(docId));
    const newMap = new Map(itemsMap);
    newMap.set(docMapKey(docId), { ...item, name: newLabel });
    setItemsMap(newMap);
  }
};

// Pure mapping helper: return tabs with updated label (no side effects)
export const updateTabLabelLocal = (tabs, tabId, newLabel) =>
  tabs.map(t => (t.id === tabId ? { ...t, label: newLabel } : t));

// Find a tab by docId
export const findTabByDocId = (tabs, docId) =>
  tabs.find(t => t.docId === docId) || null;

// Open a doc in tabs with mode 'new' or 'replace'
export const openDocInTabs = (tabs, activeTabId, doc, mode = 'replace') => {
  if (!doc) return { tabs, activeTabId };

  if (mode === 'new' || tabs.length === 0) {
    const newTab = createTab(doc);
    const existingIds = new Set((Array.isArray(tabs) ? tabs : []).map(t => String(t?.id || '')));
    const normalizedTab = existingIds.has(String(newTab.id))
      ? { ...newTab, id: makeCollisionSafeTabId(newTab.id, existingIds) }
      : newTab;
    return { tabs: [...tabs, normalizedTab], activeTabId: normalizedTab.id };
  }

  // mode === 'replace'
  const targetTabId =
    activeTabId && tabs.find(t => t.id === activeTabId)?.id
      ? activeTabId
      : tabs[0].id;

  const updatedTabs = tabs.map(t =>
    t.id === targetTabId
      ? {
          ...t,
          docId: doc.id,
          label: doc.name || 'Untitled Doc',
          readOnly: Boolean(doc.readOnly) || doc.docType === 'chat',
          toolbarVariant: doc.toolbarVariant || null,
          docType: doc.docType || null
        }
      : t
  );

  return { tabs: updatedTabs, activeTabId: targetTabId };
};

export const buildRestoredTabs = ({ savedTabs = [], itemsMap, fallbackDocs = [] } = {}) => {
  const normalizedSavedTabs = Array.isArray(savedTabs) ? savedTabs : [];
  const fallbackDocsById = new Map(
    (Array.isArray(fallbackDocs) ? fallbackDocs : []).map((doc) => [String(doc?.id), doc])
  );
  const usedIds = new Set();

  return normalizedSavedTabs
    .map((tabData) => {
      const docId = typeof tabData === 'object' ? tabData.docId : tabData;
      const pane = typeof tabData === 'object' ? tabData.pane : 'left';
      if (docId == null) return null;

      const docFromTree = findItem(itemsMap, docId, 'doc');
      const docFromFallback = fallbackDocsById.get(String(docId));
      const doc = docFromTree || docFromFallback;
      if (!doc) return null;

      const baseId = `tab-${docId}`;
      const id = makeCollisionSafeTabId(baseId, usedIds);
      usedIds.add(id);

      return {
        id,
        docId,
        label: doc.name || 'Untitled Doc',
        pane,
        docType: doc.docType || null,
        readOnly: Boolean(doc.readOnly) || doc.docType === 'chat'
      };
    })
    .filter(Boolean);
};
